import React from 'react';
import FileHoverPreview from '../common/FileHoverPreview';
import LinkHoverPreview from '../common/LinkHoverPreview';
import FileNameWithExtension from '../common/FileNameWithExtension';
import SharedDragGrip from './SharedDragGrip';
import { useAttachmentTouchInteraction } from '../../utils/sharedFilesTouchDrag';
import { resolveAttachmentId } from '../../utils/sharedFilesDrag';
import { isLinkAttachment, getLinkDisplayTitle, decodeLinkHref } from '../../utils/fileUtils';

const linkFromAttachment = (attachment) => ({
  url: attachment.fileUrl,
  title: attachment.fileName,
  customTitle: attachment.linkCustomTitle,
  description: attachment.linkDescription,
  imageUrl: attachment.linkImageUrl,
  siteName: attachment.linkSiteName,
  domain: attachment.linkDomain,
});

const formatFileSize = (bytes) => {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / k ** i).toFixed(1))} ${sizes[i]}`;
};

const GroupResourceListRow = ({
  attachment,
  index,
  messageId,
  editable,
  canDrag,
  coarsePointer,
  dragIndex,
  overIndex,
  onRemove,
  onTouchAttachmentDrop,
  onTouchHighlight,
  canRename = false,
  onRename,
  onItemDragStart,
  onItemDragOver,
  onItemDrop,
  onDragEnd,
  renderFileIcon,
}) => {
  const isLink = isLinkAttachment(attachment);
  const linkHref = decodeLinkHref(attachment.fileUrl);
  const payload = {
    sourceMessageId: messageId,
    attachmentId: resolveAttachmentId(attachment),
    itemType: 'attachment',
  };

  const handleOpen = () => {
    if (linkHref) {
      window.open(linkHref, '_blank', 'noopener,noreferrer');
    }
  };

  const handleLinkClick = (e) => {
    e.stopPropagation();
  };

  const touchHandlers = useAttachmentTouchInteraction({
    enabled: coarsePointer && editable && canDrag,
    payload,
    onTap: handleOpen,
    onDrop: onTouchAttachmentDrop,
    onHighlight: onTouchHighlight,
  });

  const canMouseDrag = editable && canDrag && !coarsePointer;
  const showDragGrip = editable && canDrag;
  const isDragging = dragIndex === index;
  const isDropOver = overIndex === index;

  const rowInner = (
    <div
      draggable={canMouseDrag}
      onDragStart={canMouseDrag ? (e) => onItemDragStart(e, index) : undefined}
      onDragEnd={canMouseDrag ? onDragEnd : undefined}
      onDragOver={editable ? (e) => onItemDragOver(e, index) : undefined}
      onDrop={editable ? (e) => onItemDrop(e, index) : undefined}
      onClick={!coarsePointer && isLink ? handleOpen : undefined}
      onDoubleClick={!coarsePointer && !isLink ? handleOpen : undefined}
      data-shared-files-drop={messageId}
      data-drop-index={index}
      className={`group/row relative flex items-center gap-2 w-full rounded-lg border px-2 py-1.5 transition-shadow ${
        isLink && !canMouseDrag ? 'cursor-pointer' : ''
      } ${
        canMouseDrag ? 'cursor-grab active:cursor-grabbing' : ''
      } ${
        isDragging
          ? 'opacity-50 border-primary-400 bg-primary-50/50 dark:bg-primary-900/20'
          : isDropOver
            ? 'border-primary-500 shadow-md ring-2 ring-primary-300/50 bg-primary-50/30 dark:bg-primary-900/15'
            : 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800/60 hover:bg-gray-50 dark:hover:bg-gray-700/40'
      }`}
    >
      {showDragGrip && (
        <div className="absolute left-1 top-1/2 -translate-y-1/2 z-10 hidden group-hover/row:flex pointer-events-none group-hover/row:pointer-events-auto">
          <SharedDragGrip
            size="sm"
            title={coarsePointer ? 'Long-press to drag' : 'Drag to move'}
          />
        </div>
      )}

      <div className="flex-shrink-0 w-9 h-9 rounded-md border border-gray-200 dark:border-gray-600 overflow-hidden flex items-center justify-center bg-gray-100 dark:bg-gray-800">
        {isLink ? (
          attachment.linkImageUrl ? (
            <img
              src={attachment.linkImageUrl}
              alt=""
              className="w-full h-full object-cover pointer-events-none select-none"
            />
          ) : (
            <svg className="w-5 h-5 text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
            </svg>
          )
        ) : (
          renderFileIcon?.(attachment)
        )}
      </div>

      <div className="flex-1 min-w-0">
        {isLink ? (
          <>
            <a
              href={linkHref}
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleLinkClick}
              className="text-xs font-semibold text-gray-900 dark:text-white truncate hover:text-primary-600 dark:hover:text-primary-400 hover:underline"
              title={getLinkDisplayTitle(linkFromAttachment(attachment))}
            >
              {getLinkDisplayTitle(linkFromAttachment(attachment))}
            </a>
            <a
              href={linkHref}
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleLinkClick}
              className="text-[10px] text-primary-600 dark:text-primary-400 truncate hover:underline"
            >
              {attachment.linkSiteName || attachment.linkDomain || linkHref}
            </a>
          </>
        ) : (
          <>
            <FileNameWithExtension
              fileName={attachment.fileName}
              nameClassName="text-xs font-semibold text-gray-900 dark:text-white"
            />
            <p className="text-[10px] text-gray-500 dark:text-gray-400">
              {formatFileSize(attachment.fileSize)}
            </p>
          </>
        )}
      </div>

      {coarsePointer && (isLink || (editable && canDrag)) && (
        <button
          type="button"
          className="absolute inset-0 z-[1] cursor-pointer bg-transparent touch-manipulation"
          aria-label={isLink ? 'Open link' : `Open ${attachment.fileName}`}
          onClick={isLink ? handleOpen : undefined}
          {...(!isLink ? touchHandlers : {})}
        />
      )}

      {editable && onRemove && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onRemove(index);
          }}
          className="relative z-[2] flex-shrink-0 p-1 rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 opacity-0 pointer-events-none transition-opacity group-hover/row:opacity-100 group-hover/row:pointer-events-auto"
          title="Remove from group"
          aria-label={`Remove ${attachment.fileName}`}
          draggable={false}
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}
    </div>
  );

  if (isLink) {
    const link = linkFromAttachment(attachment);
    return (
      <LinkHoverPreview
        link={link}
        href={linkHref}
        className="block w-full"
        canRename={canRename}
        onRename={onRename ? (l, title) => onRename(attachment, title) : undefined}
      >
        {rowInner}
      </LinkHoverPreview>
    );
  }

  return (
    <FileHoverPreview
      attachment={attachment}
      className="block w-full"
      canRename={canRename}
      onRename={onRename}
    >
      {rowInner}
    </FileHoverPreview>
  );
};

export default GroupResourceListRow;
